/**
 * One full conversation, on its own page — the read behind a feedback item's
 * "View full conversation" link. Rendered through the design system's
 * Chat.Transcript; the rated turn is highlighted and scrolled into view.
 */
import { useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { Banner, Chat, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import type { ReviewTranscriptMessage } from '../../lib/types.js';
import { useReviewTranscript } from './useFeedbackLogic.js';

const retrievalLine = (message: ReviewTranscriptMessage | undefined) =>
  message?.retrieval && message.retrieval.length > 0 ? (
    <p className="mt-1 text-xs text-ink-muted">
      Retrieved: {message.retrieval.map((r) => r.subjectName ?? r.title ?? 'unknown').join(', ')}
    </p>
  ) : null;

export const ConversationPage = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const [search] = useSearchParams();
  const ratedMessageId = search.get('m') ?? '';
  const query = useReviewTranscript(
    conversationId ?? '',
    ratedMessageId,
    false,
    Boolean(conversationId),
  );
  const byId = new Map((query.data?.messages ?? []).map((m) => [m.id, m]));

  return (
    <AppShell>
      <Breadcrumbs trail={[{ label: 'Feedback', to: '/feedback' }]} />
      <h2 className="text-ink text-2xl font-bold">Conversation</h2>
      <p className="text-ink-muted mb-5 mt-1.5 text-sm">
        The full thread behind the rated turn — highlighted below.
      </p>

      {query.isLoading && <Skeleton className="h-48 w-full rounded-xl" />}
      {query.isError && (
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>
              {query.error instanceof Error ? query.error.message : 'Failed to load'}
            </Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {query.data && (
        <div className="max-w-[82ch]">
          {/* revealHighlighted (DS 2.1.0) brings the rated turn into view once
              it renders — scoped to this list and robust to late content,
              unlike the document-wide querySelector effect it replaces. */}
          <Chat.MessageList revealHighlighted className="p-0">
            <Chat.Transcript
              messages={query.data.messages}
              highlightMessageId={ratedMessageId}
              renderMessageExtra={(m) => retrievalLine(byId.get(m.id))}
            />
          </Chat.MessageList>
        </div>
      )}
    </AppShell>
  );
};
